import './index.scss'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageTitle} from '../../components/core/title'
import DataSwitcher from '../../components/core/dataSwitcher'
import {useDispatch, useSelector} from 'react-redux'
import {getResources} from '../../stores/resources'
import {useEffect, useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import NavigationHelper from '../../utils/navigation'
import {routes} from '../../routes'
import classNames from 'classnames'
import {ImgButton} from '../../components/core/button'
import {EditIcon, HorizontalMoreIcon, PlusIcon, StatusIoIcon, StatuspageIoIcon, TrashIcon, TrendingUpIcon} from '../../icons'
import {setConfirmIsActive} from '../../stores/alert'
import Footer from '../../components/layouts/footer'
import Modal from '../../components/core/modal'
import AddIntegration from './AddIntegration'
import {deleteResourceIntegrations, getIntegrations, getResourceIntegrations} from '../../stores/integration'
import Table from '../../components/core/table'
import EmptyContent from '../resources/EmptyContent'
import ContentLoader from '../../components/core/loader'
import Badge from '../../components/core/badge'
import Confirm from '../../components/core/confirm'
import Dropdown from '../../components/core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import EmptyIntegration from './EmptyIntegration'
import Text from '../../utils/text'
import Breadcrumb from '../../components/core/breadcrumb'
import integrationTypes from '../../data/integrationTypes.json'
import {colors} from '../../utils/colors'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../utils/env'

const {isReadOnlyModeEnabled} = envConfig

const ResourceIntegrations = () => {
    const dispatch = useDispatch()
    const {id} = useParams()
    const text = useNamespaceTranslations('resource-integrations')
    const resources = useSelector(state => state.resources.resources)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [resourceIntegrations, setResourceIntegrations] = useState([])
    const [integrations, setIntegrations] = useState([])
    const [addModal, setAddModal] = useState(false)
    const [isFetching, setIsFetching] = useState(true)
    const [selectedIntegrations, setSelectedIntegrations] = useState([])
    const [isDeleting, setIsDeleting] = useState(false)
    const [clickedIntegration, setClickedIntegration] = useState(null)
    const integration = resourceIntegrations?.find(item => item?.integration === selectedIntegrations[0])
    const resource = resources?.find(item => item?.resource_id === id)

    const icons = {
        [integrationTypes.types.statuspageIo]: <StatuspageIoIcon />,
        [integrationTypes.types.statusIo]: <StatusIoIcon />
    }

    const tableHeader = [
        {
            label: text.integration,
            className: 'min-width'
        },
        {
            label: text.type,
            className: 'min-width'
        },
        {
            label: text.incident_name,
            className: 'min-width'
        },
        {
            label: text.status,
            className: 'min-width'
        }
    ]

    const ActionButtons = ({data}) => {
        return (
            <Dropdown menuButton={<button className={classNames('table-action-btn', {
                dark: isDarkMode
            })}><HorizontalMoreIcon/></button>}>
                <MenuItem onClick={() => {
                    setClickedIntegration(data)
                    setAddModal({isEdit: true})
                }
                } disabled={isReadOnlyModeEnabled}>
                    <span className="d-flex"><EditIcon/></span>
                    <span>{text.edit}</span>
                </MenuItem>
                <MenuDivider/>
                <MenuItem className="danger" onClick={() => {
                    setClickedIntegration(data)
                    dispatch(setConfirmIsActive(true))
                }
                } disabled={isReadOnlyModeEnabled}>
                    <span className="d-flex"><TrashIcon stroke={colors.colorRed}/></span>
                    <span>{text.delete}</span>
                </MenuItem>
            </Dropdown>
        )
    }

    const tableBody = resourceIntegrations?.map(item => {
        return [
            <span className="min-width">{item?.integration}</span>,
            <span className="min-width">
                <span className="d-flex align-items-center gap-half">
                    {icons[item?.type]}
                    {item?.brand_name}
                </span>
            </span>,
            <span className="min-width">{item?.name}</span>,
            <span className="flex-1">
                <Badge
                    color="primary">{item?.status}</Badge>
                <ActionButtons data={item}/>
            </span>
        ]
    })

    const fetchResources = () => {
        dispatch(getResources(true))
    }

    const deleteIntegration = async() => {
        setIsDeleting(true)
        const success = await dispatch(deleteResourceIntegrations(id, clickedIntegration?.integration || selectedIntegrations[0]))
        setIsDeleting(false)
        if (success) {
            setClickedIntegration(null)
            dispatch(setConfirmIsActive(false))
            fetchResourceIntegrations(false)
            setSelectedIntegrations([])
        }
    }

    const fetchResourceIntegrations = async(showloader = false) => {
        setIsFetching(showloader)
        const data = await dispatch(getResourceIntegrations(id))
        setResourceIntegrations(data)
        setIsFetching(false)
    }

    const fetchIntegrations = async() => {
        const data = await dispatch(getIntegrations(false))
        setIntegrations(data)
    }

    const closeModal = () => {
        setAddModal(false)
        setClickedIntegration(null)
        fetchResourceIntegrations(false)
    }

    const openModal = () => {
        setAddModal(true)
    }

    useEffect(() => {
        setSelectedIntegrations([])
        fetchResourceIntegrations(true)
    }, [id])

    useEffect(() => {
        fetchResources()
        fetchIntegrations()
    }, [])

    return (
        <main className={classNames('main resource-integrations', {
            dark: isDarkMode
        })}>
            <Modal closeModal={() => {
                setAddModal(false)
                setClickedIntegration(null)
            }} contentClassName="overflow-auto" isOpen={Boolean(addModal) && integrations?.length}
            title={addModal?.isEdit ? text.edit_integration : text.add_integration}>
                <AddIntegration
                    closePopup={closeModal}
                    modal={addModal}
                    integration={clickedIntegration || integration}
                    initialIntegrations={integrations}/>
            </Modal>
            <Confirm
                body={text.are_you_sure}
                callback={deleteIntegration} showLoader={isDeleting}/>
            {!(integrations?.length > 0) &&
                <Modal title={text.no_data} closeModal={() => setAddModal(false)}
                    isOpen={Boolean(addModal)}>
                    <EmptyIntegration close={() => {
                        setAddModal(false)
                    }}/>
                </Modal>
            }
            <div>
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <Breadcrumb data={[text.section, routes.resources]}/>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                </div>
                {isFetching ? (
                    <ContentLoader/>
                ) : (
                    <>
                        <div className="search-filter-actions">
                            <div className="header-container">
                                {resources?.length > 0 && (
                                    <div className="position-relative resource-container">
                                        <div className={classNames('resource-name', {
                                            dark: isDarkMode
                                        })}>
                                            <h2>{resource?.name}</h2>
                                            <DataSwitcher>
                                                {resources?.map((item, index) => (
                                                    <Link
                                                        to={NavigationHelper.getNavigationUrl(
                                                            NavigationHelper.getLocalizedPath(routes.resource_integrations.paths),
                                                            ':id',
                                                            item?.resource_id)}
                                                        key={index}
                                                        className={classNames({
                                                            'popover-item': true,
                                                            active: id === item?.resource_id
                                                        })}>{item.name}</Link>
                                                ))}
                                            </DataSwitcher>
                                            <Link
                                                className={classNames('table-action-btn', {
                                                    dark: isDarkMode
                                                })}
                                                to={NavigationHelper.getNavigationUrl(
                                                    NavigationHelper.getLocalizedPath(routes.monitoring.paths),
                                                    ':id',
                                                    id)}>
                                                <TrendingUpIcon/>
                                            </Link>
                                        </div>
                                        <Link
                                            to={resource?.url} className={classNames('url', {
                                                dark: isDarkMode
                                            })}>{Text.textLengthChecker(resource?.url, 21)}</Link>
                                    </div>
                                )}
                                <div className="selected-actions">
                                    <ImgButton className={classNames({
                                        'light-bg': !isDarkMode
                                    })}  onClick={openModal} disabled={isReadOnlyModeEnabled}>
                                        <PlusIcon stroke={colors.colorMuted}/>
                                        <span className="button-text">{text.new}</span>
                                    </ImgButton>
                                    <ImgButton className={classNames({
                                        'light-bg': !isDarkMode
                                    })}  disabled={selectedIntegrations.length !== 1 || isReadOnlyModeEnabled}
                                    onClick={() => setAddModal({isEdit: true})}>
                                        <span><EditIcon/></span>
                                        <span className="button-text">{text.edit}</span>
                                    </ImgButton>
                                    <ImgButton className={classNames('outline-red', {
                                        'light-bg': !isDarkMode
                                    })}
                                    disabled={selectedIntegrations.length !== 1 || isReadOnlyModeEnabled}
                                    onClick={() => dispatch(setConfirmIsActive(true))}>
                                        <span><TrashIcon stroke={colors.colorRed}/></span>
                                        <span className="button-text">{text.delete}</span>
                                    </ImgButton>
                                </div>
                            </div>
                        </div>
                        {resourceIntegrations?.length > 0 ? (
                            <Table
                                tableHeader={tableHeader}
                                tbodyData={tableBody}
                                identificator='integration'
                                hasCheckbox={true}
                                strech
                                data={resourceIntegrations}
                                selectedData={selectedIntegrations}
                                setSelectedData={data => setSelectedIntegrations(data)}
                            />
                        ) : (
                            <EmptyContent message={text.no_data} hideButton={true}/>
                        )}
                    </>
                )}
            </div>
            <Footer/>
        </main>
    )
}

export default ResourceIntegrations
